import type { ZodType } from 'zod';
import { lavoratoriApi } from './api/lavoratori.ts';
import { impreseApi } from './api/imprese.ts';
import { delegheApi } from './api/deleghe.ts';
import { codeApi } from './api/code.ts';
import { confrontiApi } from './api/confronti.ts';
import { disdetteApi } from './api/disdette.ts';
import { elaborazioniApi } from './api/elaborazioni.ts';
import { statisticheApi } from './api/statistiche.ts';
import { presentazioniApi } from './api/presentazioni.ts';
import { utentiApi } from './api/utenti.ts';
import { operatoriApi } from './api/operatori.ts';
import { archivioApi } from './api/archivio.ts';
import { listeApi } from './api/liste.ts';
import { delegheAltriTerritoriApi } from './api/deleghe-altri-territori.ts';
import { territoriApi } from './api/territori.ts';

export type ApiResult<T = unknown> =
  | { ok: true; data: T }
  | { ok: false; error: string; errori?: Array<{ campo: string; messaggio: string }>; status?: number; body?: unknown };

/**
 * Messaggio d'errore della risposta: `{ error }` delle route dell'app, oppure `message`
 * nel formato Fastify `{ statusCode, error: 'Bad Request', message }` (reply.badRequest & co.),
 * dove `error` è solo la frase HTTP generica.
 */
const messaggioErrore = (body: Record<string, unknown>): string | undefined => {
  if (typeof body.statusCode === 'number' && typeof body.message === 'string') return body.message;
  return typeof body.error === 'string' ? body.error : undefined;
};

const apiFetch = async <T = unknown>(
  path: string,
  options?: RequestInit
): Promise<ApiResult<T>> => {
  const url = path.startsWith('/api/') ? path : `/api/${path}`;

  const headers = new Headers(options?.headers);
  if (options?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      credentials: 'same-origin',
    });
  } catch {
    return { ok: false, error: 'Errore di rete' };
  }

  if (response.status === 401) {
    window.location.href = '/auth/login';
    return { ok: false, error: 'Sessione scaduta', status: 401 };
  }

  if (!response.ok) {
    const generico = `Richiesta non riuscita (errore ${response.status})`;
    try {
      const body = (await response.json()) as Record<string, unknown>;
      const errori = Array.isArray(body.errori)
        ? body.errori as Array<{ campo: string; messaggio: string }>
        : undefined;
      return { ok: false, error: messaggioErrore(body) ?? generico, errori, status: response.status, body };
    } catch {
      return { ok: false, error: generico, status: response.status };
    }
  }

  let data: T;
  try {
    data = (await response.json()) as T;
  } catch {
    return { ok: false, error: 'Risposta non valida dal server' };
  }
  return { ok: true, data };
};

// --- Helper base (senza validazione Zod) ---

export const apiGet = <T = unknown>(path: string): Promise<ApiResult<T>> =>
  apiFetch<T>(path);

export const apiPost = <T = unknown>(path: string, body: unknown): Promise<ApiResult<T>> =>
  apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });

export const apiPut = <T = unknown>(path: string, body: unknown): Promise<ApiResult<T>> =>
  apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) });

export const apiDelete = <T = unknown>(path: string): Promise<ApiResult<T>> =>
  apiFetch<T>(path, { method: 'DELETE' });

export const apiPatch = <T = unknown>(path: string, body: unknown): Promise<ApiResult<T>> =>
  apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

// --- Helper con validazione Zod ---

const validatedFetch = async <T>(
  method: string,
  path: string,
  schema: ZodType<T>,
  body?: unknown
): Promise<ApiResult<T>> => {
  const opts: RequestInit | undefined = body !== undefined
    ? { method, body: JSON.stringify(body) }
    : method !== 'GET' ? { method } : undefined;
  const result = await apiFetch<unknown>(path, opts);
  if (!result.ok) return result;
  const parsed = schema.safeParse(result.data);
  if (!parsed.success) {
    console.error(`[api] Risposta non conforme: ${method} ${path}`, parsed.error.issues);
    return { ok: false, error: 'Risposta API non conforme allo schema' };
  }
  return { ok: true, data: parsed.data };
};

export const validatedGet = <T>(path: string, schema: ZodType<T>): Promise<ApiResult<T>> =>
  validatedFetch('GET', path, schema);

export const validatedPost = <T>(path: string, body: unknown, schema: ZodType<T>): Promise<ApiResult<T>> =>
  validatedFetch('POST', path, schema, body);

export const validatedPut = <T>(path: string, body: unknown, schema: ZodType<T>): Promise<ApiResult<T>> =>
  validatedFetch('PUT', path, schema, body);

export const validatedDelete = <T>(path: string, schema: ZodType<T>): Promise<ApiResult<T>> =>
  validatedFetch('DELETE', path, schema);

export const validatedPatch = <T>(path: string, body: unknown, schema: ZodType<T>): Promise<ApiResult<T>> =>
  validatedFetch('PATCH', path, schema, body);

export const apiClient = {
  archivio: archivioApi,
  code: codeApi,
  confronti: confrontiApi,
  deleghe: delegheApi,
  delegheAltriTerritori: delegheAltriTerritoriApi,
  disdette: disdetteApi,
  elaborazioni: elaborazioniApi,
  imprese: impreseApi,
  lavoratori: lavoratoriApi,
  liste: listeApi,
  statistiche: statisticheApi,
  territori: territoriApi,
  presentazioni: presentazioniApi,
  utenti: utentiApi,
  operatori: operatoriApi,
} as const;
